import { Box, Grid, Tooltip } from "@mui/material";
import { PositionBadge } from "../../CrewSchedule/PositionBadge";
import { FlightChip } from "../../Flights/FlightChip";
import moment from "moment-timezone";
import { IFlightFlag, IFlightFlagSubDoc } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { IContentCellProps } from "src/pages/FlightStatus/FlightStatusWindow";
import { Fragment } from "react";

export function FlightInfo(props: IContentCellProps): JSX.Element {
  const { data, flightId, useUTC, flightFilter, setFlightFilter } = props;
  const flightFlags = useSelector((state: IRootState) => state.structures.customerStructure.flightFlags);
  const flightChipData = {
    _id: flightId,
    flightNumber: data.flightNumber,
    origin: data.origin.icao,
    destination: data.destination.icao,
    flightType: data.flightType,
    aircraft: data.aircraft,
  };
  const originTz = useUTC === "UTC" ? "UTC" : data.origin.timezone;
  const destinationTz = useUTC === "UTC" ? "UTC" : data.destination.timezone;
  const duration = moment.tz(data.actualTimes.in, originTz).diff(moment.tz(data.actualTimes.out, originTz), "minutes");
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  const timeFormat = "DD/HH:mm";
  return (
    <Grid container spacing={0} sx={{ alignItems: "center" }}>
      <Grid
        item
        xs="auto"
        onDoubleClick={(e: any) => {
          const filteredFlights = [...flightFilter];
          const flightIdx = filteredFlights.findIndex((flight) => {
            return (flight.hasOwnProperty("flight") && flightId === flight.flight._id) || flightId === flight._id;
          });
          if (flightIdx >= 0) {
            filteredFlights.splice(flightIdx, 1);
          } else {
            filteredFlights.push(flightChipData);
          }
          setFlightFilter(filteredFlights);
        }}
      >
        <FlightChip data={flightChipData} disableContext={false} disableDrag={true} />
      </Grid>
      <Grid item xs sx={{ display: "flex", justifyContent: "start", pl: 1.6 }}>
        {data.aircraft.registration}
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <Grid item xs={12} sx={{ fontSize: "0.8em" }}>
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={"auto"}
                  sx={(theme) => ({
                    display: "flex",
                    justifyContent: "center",
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      py: "2px",
                      px: "4px",
                      width: "100%",
                      display: "inline-flex",
                      fontWeight: 700,
                    })}
                  >
                    S:
                    <Box
                      sx={(theme) => ({
                        px: 1,
                        borderRadius: "2px",
                      })}
                    >
                      <Tooltip title="Scheduled Out">
                        <Box>{moment.tz(data.scheduledTimes.out, originTz).format(timeFormat)}</Box>
                      </Tooltip>
                    </Box>
                    {" - "}
                    <Box
                      sx={(theme) => ({
                        px: 1,
                        borderRadius: "2px",
                      })}
                    >
                      <Tooltip title="Scheduled In">
                        <Box>{moment.tz(data.scheduledTimes.in, destinationTz).format(timeFormat)}</Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ fontSize: "0.8em" }}>
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={"auto"}
                  sx={(theme) => ({
                    display: "flex",
                    justifyContent: "center",
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      py: "2px",
                      px: "4px",
                      width: "100%",
                      display: "inline-flex",
                      fontWeight: 700,
                    })}
                  >
                    A:
                    <Box
                      sx={(theme) => ({
                        px: 1,
                        borderRadius: "2px",
                      })}
                    >
                      <Tooltip title="Actual Out">
                        <Box>{moment.tz(data.actualTimes.out, originTz).format(timeFormat)}</Box>
                      </Tooltip>
                    </Box>
                    {" - "}
                    <Box
                      sx={(theme) => ({
                        px: 1,
                        borderRadius: "2px",
                      })}
                    >
                      <Tooltip title="Actual In">
                        <Box>{moment.tz(data.actualTimes.in, destinationTz).format(timeFormat)}</Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ fontSize: "0.8em", textAlign: "center" }}>
              Dur: {hours}:{minutes}
            </Grid>
          </Grid>
          <Grid item xs={6} sx={{ pl: 1 }}>
            <Grid container spacing={0}>
              <Grid item xs={7} sx={{ pl: 1 }}>
                {data.crew.length > 0 &&
                  data.crew.map((crewPos: any, key: number) => {
                    return (
                      <Tooltip key={key} title={`${crewPos.crew.firstName} ${crewPos.crew.lastName}`}>
                        <Box sx={{ m: 0, display: "inline-flex" }}>
                          <PositionBadge filled={true} positionTitle={crewPos.position} />
                        </Box>
                      </Tooltip>
                    );
                  })}
              </Grid>
              <Grid item xs={5} sx={{ display: "flex", justifyContent: "end", mt: "4px" }}>
                {data.flags &&
                  data.flags.length > 0 &&
                  data.flags.map((flag: IFlightFlagSubDoc, key: number) => {
                    const fullFlightFlag = flightFlags.find((structureFlag: IFlightFlag) => {
                      return flag._id === structureFlag._id;
                    });
                    if (fullFlightFlag) {
                      return (
                        <Tooltip title={flag.title} key={key}>
                          <Box sx={{ mr: "3px", display: "inline-flex" }}>
                            <div dangerouslySetInnerHTML={{ __html: fullFlightFlag.icon }} />
                          </Box>
                        </Tooltip>
                      );
                    } else {
                      return <Fragment key={key}></Fragment>;
                    }
                  })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
